import { Injectable } from "@angular/core";
import { Observable, map } from "rxjs";
import { ModulesWithPages } from "./cases.service";
import { CaseModulePatchQPS, CaseModulePostQPS, CaseModulePostResponse, modulesGetQPS } from "src/app/shared/models/modulesQPS";
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from "@angular/common/http";
import { RestUtilitiesService } from "./rest-utilities.service";
import { environment } from "src/environments/environment";
import { APIS } from "../enums/apis";
import { CaseModule, CaseModuleComposed, Module } from "src/app/shared/models/module";
import { DateRange } from "moment-range";
import moment from "moment";
import { CaseDateFilters } from "src/app/abstraction/cases.facade";
import { ArrangementsService } from "./arrangements.service";
import { ArrangementsFacade } from "src/app/abstraction/arrangements.facade";

@Injectable({
    providedIn: 'root'
})
export class ModulesService {

    constructor(
        private http: HttpClient,
        private restUtilitiesService: RestUtilitiesService,
        private arrangementsFacade: ArrangementsFacade
    ) {
    }

    getModules(qps: modulesGetQPS): Observable<ModulesWithPages> {
        const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
        const queryParams: HttpParams = qps ? this.restUtilitiesService.createAndAppendQps(this.restUtilitiesService.formatQPs(qps)) : null;
        const url = `${environment.apiCaseService}${APIS.USER_MODULES}`;
        return this.http
            .get<any>(url, {
                headers: queryHeaders,
                observe: 'response',
                params: queryParams
            })
            .pipe<any>(
                map<HttpResponse<any>, any>((response) => {
                    return response.body;
                }),
            );
    }

    getModulesLight(qps: modulesGetQPS): Observable<Module[]> {
        const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
        const queryParams: HttpParams = qps ? this.restUtilitiesService.createAndAppendQps(this.restUtilitiesService.formatQPs(qps)) : null;
        const url = `${environment.apiCaseService}${APIS.USER_MODULES_LIGHT}`;
        return this.http
            .get<any>(url, {
                headers: queryHeaders,
                observe: 'response',
                params: queryParams
            })
            .pipe<any>(
                map<HttpResponse<any>, any>((response) => {
                    return response.body.data;
                }),
            );
    }

    postModule(module: Module): Observable<Module> {
        const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
        const queryParams: HttpParams = module ? this.restUtilitiesService.createAndAppendQps(this.restUtilitiesService.formatQPs(module)) : null;
        const url = `${environment.apiCaseService}${APIS.USER_MODULES}`;
        return this.http
            .post<Module>(url, module, {
                headers: queryHeaders,
                observe: 'response',
            })
            .pipe<Module>(
                map<HttpResponse<Module>, Module>((response) => {
                    return response.body as Module;
                }),
            );
    }

    patchModule(module: Module, id: number): Observable<Module> {
        const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
        const queryParams: HttpParams = module ? this.restUtilitiesService.createAndAppendQps(this.restUtilitiesService.formatQPs(module)) : null;
        const url = `${environment.apiCaseService}${APIS.USER_MODULES}/${id}`;
        return this.http
            .patch<Module>(url, module, {
                headers: queryHeaders,
                observe: 'response',
            })
            .pipe<Module>(
                map<HttpResponse<Module>, Module>((response) => {
                    return response.body as Module;
                }),
            );
    }

    getCaseModules(caseId: number): Observable<CaseModule[]> {
        const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
        // const queryParams: HttpParams = qps ? this.restUtilitiesService.createAndAppendQps(this.restUtilitiesService.formatQPs(qps)) : null;
        const url = `${environment.apiCaseService}${APIS.CASES}/${caseId}/${APIS.MODULES}`;
        return this.http
            .get<any>(url, {
                headers: queryHeaders,
                observe: 'response',
                // params: queryParams
            })
            .pipe<any>(
                map<HttpResponse<any>, any>((response) => {
                    return response.body.data;
                }),
            );
    }

    postCaseModule(qps: CaseModulePostQPS, caseId: Number, moduleId: number): Observable<CaseModulePostResponse> {
        const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
        const url = `${environment.apiCaseService}${APIS.CASES}/${caseId}/${APIS.MODULES}/${moduleId}/provisions/bulk`;
        return this.http
            .post<any>(url, JSON.stringify(qps), {
                headers: queryHeaders,
                observe: 'response',
            })
            .pipe<any>(
                map<HttpResponse<any>, any>((response) => {
                    return response.body as CaseModulePostResponse;
                }),
            );
    }

    patchCaseModule(qps: CaseModulePatchQPS, caseId: Number, caseModuleId: number): Observable<CaseModulePostResponse> {
        const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
        const url = `${environment.apiCaseService}${APIS.CASES}/${caseId}/${APIS.CASE_MODULES}/${caseModuleId}`;
        return this.http
            .patch<any>(url, JSON.stringify(qps), {
                headers: queryHeaders,
                observe: 'response',
            })
            .pipe<any>(
                map<HttpResponse<any>, any>((response) => {
                    return response.body as CaseModulePostResponse;
                }),
            );
    }

    deleteCaseModule(caseId: Number, caseModuleId: number, force: boolean = false): Observable<void> {
        const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
        const queryParams = new HttpParams().set('force', String(force));
        const url = `${environment.apiCaseService}${APIS.CASES}/${caseId}/${APIS.CASE_MODULES}/${caseModuleId}`;
        return this.http
            .delete<void>(url, {
                headers: queryHeaders,
                observe: 'response',
                params: queryParams,
            })
            .pipe<void>(
                map<HttpResponse<void>, void>((response) => {
                    return;
                }),
            );
    }

    getModulesCount(): Observable<any> {
        const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
        return this.http
            .get<any>(`${environment.apiCaseService}${APIS.USER_MODULES}/count`, {
                headers: queryHeaders,
                observe: 'response',
            })
            .pipe<any>(
                map<HttpResponse<any>, any>((response) => {
                    return response.body;
                }),
            );
    }

    public filterModulesByDateRange(range: CaseDateFilters, modules: CaseModuleComposed[]): CaseModuleComposed[] {

        if (!range?.historyFromDate || !range?.historyToDate) return modules ?? [];

        const rangeStart = moment(range.historyFromDate).startOf('day');
        const rangeEnd = moment(range.historyToDate).endOf('day');

        return (modules ?? []).filter(m => {
            const vigency = String(m?.vigency ?? '').trim();
            if (!vigency) return true;

            const parts = vigency.split(' - ');
            if (parts.length !== 2) return true;

            const moduleFrom = moment(parts[0], 'DD/MM/YYYY', true).startOf('day');
            const moduleTo = moment(parts[1], 'DD/MM/YYYY', true).endOf('day');

            if (!moduleFrom.isValid() || !moduleTo.isValid()) return true;

            // Intersección: muestra si el rango del módulo cruza el rango seleccionado
            return moduleFrom.isSameOrBefore(rangeEnd) && moduleTo.isSameOrAfter(rangeStart);
        });
    }


}
